/**
 * Value type coercion and validation utilities for metadata values.
 * Based on API formatting rules:
 * - integer/float: numeric input required
 * - boolean: accepts true/false style inputs, stores canonical "true"/"false"
 * - date: ISO YYYY-MM-DD
 * - datetime: ISO datetime (supports Z, normalized to ISO)
 * - string: any text value
 */

export const VALUE_TYPES = {
  STRING: 'string',
  INTEGER: 'integer',
  FLOAT: 'float',
  BOOLEAN: 'boolean',
  DATE: 'date',
  DATETIME: 'datetime',
};

/**
 * Get list of available value types
 */
export function getValueTypes() {
  return [
    { label: 'Text', value: VALUE_TYPES.STRING },
    { label: 'Whole Number', value: VALUE_TYPES.INTEGER },
    { label: 'Decimal Number', value: VALUE_TYPES.FLOAT },
    { label: 'Yes/No', value: VALUE_TYPES.BOOLEAN },
    { label: 'Date', value: VALUE_TYPES.DATE },
    { label: 'Date & Time', value: VALUE_TYPES.DATETIME },
  ];
}

/**
 * Get a user-friendly label for a value type
 * @param {string} valueType - The technical value type
 * @returns {string} User-friendly label
 */
export function getValueTypeLabel(valueType) {
  const types = getValueTypes();
  const found = types.find(t => t.value === valueType);
  return found ? found.label : valueType || 'Text';
}

/**
 * Coerce and validate a value based on its type
 * @param {*} value - The value to coerce
 * @param {string} valueType - The target value type
 * @returns {object} { valid: boolean, coercedValue: any, error: string|null }
 */
export function coerceValue(value, valueType) {
  // Handle null/empty values
  if (value === null || value === undefined || value === '') {
    return { valid: true, coercedValue: null, error: null };
  }

  const stringValue = String(value).trim();

  switch (valueType) {
    case VALUE_TYPES.STRING:
      return { valid: true, coercedValue: stringValue, error: null };

    case VALUE_TYPES.INTEGER: {
      // Check if the string represents an integer (no decimal point)
      const intRegex = /^-?\d+$/;
      if (!intRegex.test(stringValue)) {
        return { valid: false, coercedValue: null, error: 'Must be a whole number without decimals' };
      }
      const intVal = parseInt(stringValue, 10);
      if (isNaN(intVal)) {
        return { valid: false, coercedValue: null, error: 'Must be a valid integer' };
      }
      return { valid: true, coercedValue: String(intVal), error: null };
    }

    case VALUE_TYPES.FLOAT: {
      const normalizedFloat = stringValue.replace(/,/g, '.');
      const floatRegex = /^-?(?:\d+\.\d+|\d+|\.\d+)$/;
      if (!floatRegex.test(normalizedFloat)) {
        return { valid: false, coercedValue: null, error: 'Must be a valid number' };
      }
      const floatVal = parseFloat(normalizedFloat);
      if (isNaN(floatVal)) {
        return { valid: false, coercedValue: null, error: 'Must be a valid number' };
      }
      return { valid: true, coercedValue: String(floatVal), error: null };
    }

    case VALUE_TYPES.BOOLEAN: {
      const lowerVal = stringValue.toLowerCase();
      if (['true', '1', 'yes', 'on'].includes(lowerVal)) {
        return { valid: true, coercedValue: 'true', error: null };
      }
      if (['false', '0', 'no', 'off'].includes(lowerVal)) {
        return { valid: true, coercedValue: 'false', error: null };
      }
      return { valid: false, coercedValue: null, error: 'Must be true or false' };
    }

    case VALUE_TYPES.DATE: {
      // Validate ISO YYYY-MM-DD format
      const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
      if (!dateRegex.test(stringValue)) {
        return { valid: false, coercedValue: null, error: 'Must be in YYYY-MM-DD format' };
      }
      // Additional validation: check if it's a valid date
      const date = new Date(stringValue + 'T00:00:00Z');
      if (isNaN(date.getTime())) {
        return { valid: false, coercedValue: null, error: 'Must be a valid date' };
      }
      return { valid: true, coercedValue: stringValue, error: null };
    }

    case VALUE_TYPES.DATETIME: {
      // Try to parse as ISO datetime
      let isoString = stringValue;

      // If it's a local datetime (no Z), try to normalize it
      if (!isoString.includes('T')) {
        return { valid: false, coercedValue: null, error: 'Must be in ISO datetime format' };
      }

      const date = new Date(isoString);
      if (isNaN(date.getTime())) {
        return { valid: false, coercedValue: null, error: 'Must be a valid datetime' };
      }

      // Normalize to ISO format
      const normalized = date.toISOString();
      return { valid: true, coercedValue: normalized, error: null };
    }

    default:
      return { valid: false, coercedValue: null, error: `Unknown value type: ${valueType}` };
  }
}

/**
 * Format a value for display based on its type
 * @param {*} value - The value to format
 * @param {string} valueType - The value type
 * @returns {string} Formatted value for display
 */
export function formatValue(value, valueType) {
  if (value === null || value === undefined || value === '') {
    return 'No Value';
  }

  const stringValue = String(value);

  switch (valueType) {
    case VALUE_TYPES.BOOLEAN:
      return stringValue === 'true' ? 'Yes' : 'No';

    case VALUE_TYPES.DATE: {
      try {
        const date = new Date(stringValue + 'T00:00:00Z');
        return date.toLocaleDateString();
      } catch {
        return stringValue;
      }
    }

    case VALUE_TYPES.DATETIME: {
      try {
        const date = new Date(stringValue);
        return date.toLocaleString();
      } catch {
        return stringValue;
      }
    }

    default:
      return stringValue;
  }
}

/**
 * Get appropriate input type for HTML input element
 * @param {string} valueType - The value type
 * @returns {string} HTML input type
 */
export function getInputType(valueType) {
  switch (valueType) {
    case VALUE_TYPES.INTEGER:
    case VALUE_TYPES.FLOAT:
      return 'number';
    case VALUE_TYPES.DATE:
      return 'date';
    case VALUE_TYPES.DATETIME:
      return 'datetime-local';
    case VALUE_TYPES.BOOLEAN:
      return 'text'; // Will show as dropdown or toggle
    default:
      return 'text';
  }
}

/**
 * Convert datetime-local input value to ISO format
 * @param {string} localDateTime - datetime-local input value
 * @returns {string} ISO datetime string
 */
export function localDateTimeToISO(localDateTime) {
  if (!localDateTime) return null;
  const date = new Date(localDateTime);
  return date.toISOString();
}

/**
 * Convert ISO datetime to datetime-local input value
 * @param {string} isoDateTime - ISO datetime string
 * @returns {string} datetime-local format string
 */
export function isoToLocalDateTime(isoDateTime) {
  if (!isoDateTime) return '';
  const date = new Date(isoDateTime);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

/**
 * Get options for select dropdowns based on value type
 * @param {string} valueType - The value type
 * @returns {Array} Array of { label, value } options
 */
export function getOptionsForType(valueType) {
  switch (valueType) {
    case VALUE_TYPES.BOOLEAN:
      return [
        { label: 'Yes', value: 'true' },
        { label: 'No', value: 'false' },
      ];
    default:
      return [];
  }
}

/**
 * Check if a value type should use a select dropdown
 * @param {string} valueType - The value type
 * @returns {boolean} True if should use select dropdown
 */
export function shouldUseDropdown(valueType) {
  return valueType === VALUE_TYPES.BOOLEAN;
}

/**
 * Convert value to appropriate format for input element
 * @param {string} value - The stored value
 * @param {string} valueType - The value type
 * @returns {string} Value formatted for input element
 */
export function valueToInputFormat(value, valueType) {
  if (value === null || value === undefined) return '';
  
  switch (valueType) {
    case VALUE_TYPES.DATETIME:
      return isoToLocalDateTime(value);
    default:
      return String(value);
  }
}

/**
 * Convert input value to storage format
 * @param {string} inputValue - The input value
 * @param {string} valueType - The value type
 * @returns {string} Value formatted for storage
 */
export function inputToStorageFormat(inputValue, valueType) {
  if (!inputValue) return null;
  
  switch (valueType) {
    case VALUE_TYPES.DATETIME:
      return localDateTimeToISO(inputValue);
    case VALUE_TYPES.DATE:
      return inputValue; // Already in YYYY-MM-DD format
    default:
      return inputValue;
  }
}
